import { useEffect, useRef, useState } from "react";
import { haptic } from "@karrtopelka/game-kit";
import { PLINKO_MULTS, PLINKO_ROWS } from "@karrtopelka/game-api";
import { colors } from "@karrtopelka/ui";

/**
 * The Galton board, with real ballistics. The OUTCOME is the server's 12 bounce
 * bits — this component only performs them. Between peg rows the ball is a true
 * projectile: at each peg it gets an upward kick (vy₀ = −√(2g·h)) and a constant
 * lateral velocity toward the next peg (vx = Δx/T, where T solves
 * Δy = vy₀T + ½gT²). Per-row bounce heights are jittered DETERMINISTICALLY from
 * the path bits, so a replay of the same drop looks identical and endpoints never
 * drift — the ball can only land in the bucket the server scored.
 *
 * Garnish: squash & stretch at contact, peg flash on hit, a dampened settle
 * bounce in the bucket, haptic tick per row.
 */

const VIEW_W = 100;
const VIEW_H = 102;
const TOP_Y = 12; // first peg row
const ROW_DY = 6.3;
const DX = 7.2; // horizontal peg spacing
const LAST_PEG_Y = TOP_Y + (PLINKO_ROWS - 1) * ROW_DY; // 81.3
const BUCKET_TOP = LAST_PEG_Y + 7;
const SPAWN_Y = TOP_Y - 9;

// --- ballistics (view units, real seconds) ---
const G = 520; // gravity, u/s²
const BOUNCE_H = 1.9; // nominal apex above a peg, u
const SETTLE_E = 0.38; // restitution of the bucket-floor settle bounce
const BALL_R = 1.9;

interface Seg {
  /** Segment start time (s from drop start). */
  t0: number;
  dur: number;
  x0: number;
  y0: number;
  vx: number;
  vy0: number;
  /** Peg hit at the START of this segment (row index), -1 for the initial fall. */
  hitRow: number;
}

/** Deterministic 0..1 jitter per row so replays look identical. */
function jitter01(path: number[], row: number): number {
  let h = 2166136261 ^ row;
  for (let i = 0; i < path.length; i++) h = Math.imul(h ^ ((path[i]! << i) | i), 16777619);
  return ((h >>> 0) % 1000) / 1000;
}

/** Peg x for row r (0-based), col c (0..r). */
function pegX(r: number, c: number): number {
  return VIEW_W / 2 + (c - r / 2) * DX;
}

/** Build the full flight plan: spawn → row0 peg → … → last peg → bucket floor. */
function buildFlight(path: number[]): { segs: Seg[]; total: number; finalX: number } {
  const segs: Seg[] = [];
  let t = 0;
  // initial free fall onto the apex peg
  const fall = Math.sqrt((2 * (TOP_Y - SPAWN_Y)) / G);
  segs.push({ t0: 0, dur: fall, x0: VIEW_W / 2, y0: SPAWN_Y, vx: 0, vy0: 0, hitRow: -1 });
  t += fall;

  let rights = 0;
  let x = VIEW_W / 2;
  for (let row = 0; row < PLINKO_ROWS; row++) {
    const y0 = TOP_Y + row * ROW_DY;
    const dir = path[row]! === 1 ? 1 : -1;
    const isLast = row === PLINKO_ROWS - 1;
    const dy = isLast ? BUCKET_TOP - y0 : ROW_DY;
    const h = BOUNCE_H * (0.75 + 0.5 * jitter01(path, row)); // ±25% apex, deterministic
    const vy0 = -Math.sqrt(2 * G * h);
    const dur = (-vy0 + Math.sqrt(vy0 * vy0 + 2 * G * dy)) / G;
    const x1 = x + (dir * DX) / 2;
    segs.push({ t0: t, dur, x0: x, y0, vx: (x1 - x) / dur, vy0, hitRow: row });
    t += dur;
    x = x1;
    rights += path[row]! === 1 ? 1 : 0;
  }

  // settle bounce on the bucket floor (vertical only)
  const vImpact = Math.sqrt(2 * G * (BOUNCE_H + ROW_DY)); // approx
  const vUp = vImpact * SETTLE_E;
  const settleDur = (2 * vUp) / G;
  segs.push({ t0: t, dur: settleDur, x0: x, y0: BUCKET_TOP, vx: 0, vy0: -vUp, hitRow: -1 });
  t += settleDur;

  return { segs, total: t, finalX: x };
}

function bucketHeat(k: number): string {
  const d = Math.abs(PLINKO_ROWS / 2 - k);
  if (d >= 5) return colors.gold;
  if (d >= 4) return colors.win;
  if (d >= 2) return colors.text;
  return colors.textFaint;
}

/** Peg-contact polyline for the drop that just landed — a faint pink trace. */
function tracePoints(path: number[]): string {
  const pts: string[] = [`${VIEW_W / 2},${SPAWN_Y}`];
  let rights = 0;
  for (let row = 0; row < PLINKO_ROWS; row++) {
    pts.push(`${pegX(row, rights)},${TOP_Y + row * ROW_DY}`);
    rights += path[row]! === 1 ? 1 : 0;
  }
  pts.push(`${pegX(PLINKO_ROWS, rights)},${BUCKET_TOP}`);
  return pts.join(" ");
}

export function Board({
  path,
  dropId,
  onLanded,
}: {
  /** Server bounce bits; null while idle. */
  path: number[] | null;
  /** Changes per drop so the same path twice still re-animates. */
  dropId: number;
  onLanded: () => void;
}) {
  const [ball, setBall] = useState<{ x: number; y: number; squash: number } | null>(null);
  const [landedBucket, setLandedBucket] = useState<number | null>(null);
  const [flash, setFlash] = useState<{ row: number; col: number } | null>(null);
  const rafRef = useRef(0);

  useEffect(() => {
    if (!path) {
      setBall(null);
      setLandedBucket(null);
      setFlash(null);
      return;
    }
    setLandedBucket(null);
    const { segs, total } = buildFlight(path);
    const t0 = performance.now();
    let lastHit = -2;
    let done = false;

    const tick = () => {
      const t = (performance.now() - t0) / 1000;
      if (t >= total) {
        if (!done) {
          done = true;
          const rights = path.reduce((s, b) => s + b, 0);
          setBall({ x: pegX(PLINKO_ROWS, rights), y: BUCKET_TOP, squash: 1 });
          setFlash(null);
          setLandedBucket(rights);
          onLanded();
        }
        return;
      }
      // find the active segment (few enough to scan)
      let seg = segs[segs.length - 1]!;
      for (const s of segs) {
        if (t >= s.t0 && t < s.t0 + s.dur) {
          seg = s;
          break;
        }
      }
      const lt = t - seg.t0;
      const x = seg.x0 + seg.vx * lt;
      const y = seg.y0 + seg.vy0 * lt + 0.5 * G * lt * lt;
      // squash right at contact, stretch mid-flight
      const contact = lt < 0.045 && seg.hitRow >= 0;
      const squash = contact ? 0.72 : 1 + Math.min(Math.abs(seg.vy0 + G * lt) / 260, 0.18);
      setBall({ x, y, squash });

      if (seg.hitRow > lastHit && seg.hitRow >= 0) {
        lastHit = seg.hitRow;
        haptic.tap();
        // the peg it just hit: col = rights accumulated before this row
        let rights = 0;
        for (let i = 0; i < seg.hitRow; i++) rights += path[i]!;
        setFlash({ row: seg.hitRow, col: rights });
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropId]);

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      className="w-full rounded-lg border border-border bg-surface"
      aria-hidden
    >
      <defs>
        <radialGradient id="ballGrad" cx="0.35" cy="0.3" r="1">
          <stop offset="0%" stopColor={colors.accentBright} />
          <stop offset="60%" stopColor={colors.accent} />
          <stop offset="100%" stopColor={colors.accentMuted} />
        </radialGradient>
        <radialGradient id="boardGlow" cx="0.5" cy="0.1" r="1">
          <stop offset="0%" stopColor={colors.accent} stopOpacity="0.05" />
          <stop offset="70%" stopColor={colors.accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect x="0" y="0" width={VIEW_W} height={VIEW_H} fill="url(#boardGlow)" />

      {/* pegs */}
      {Array.from({ length: PLINKO_ROWS }, (_, row) =>
        Array.from({ length: row + 1 }, (_, c) => {
          const hot = flash !== null && flash.row === row && flash.col === c;
          return (
            <circle
              key={`${row}-${c}`}
              cx={pegX(row, c)}
              cy={TOP_Y + row * ROW_DY}
              r={hot ? 1.35 : 1}
              fill={hot ? colors.accent : "#3a3a44"}
              style={
                hot
                  ? { filter: `drop-shadow(0 0 2.5px ${colors.accent})` }
                  : undefined
              }
            />
          );
        }),
      )}

      {/* trace of the landed drop — the receipt of where the ball actually went */}
      {landedBucket !== null && path && (
        <polyline
          points={tracePoints(path)}
          fill="none"
          stroke={colors.accent}
          strokeOpacity={0.16}
          strokeWidth={0.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}

      {/* buckets */}
      {PLINKO_MULTS.map((m, k) => {
        const x = pegX(PLINKO_ROWS, k);
        const hot = landedBucket === k;
        const heat = bucketHeat(k);
        const jackpot = m >= 100; // rim buckets get an always-on faint gold rim
        return (
          <g
            key={k}
            style={
              hot
                ? { transform: "translateY(1.5px)", transition: "transform 120ms" }
                : undefined
            }
          >
            <rect
              x={x - DX / 2 + 0.5}
              y={BUCKET_TOP + 1.2}
              width={DX - 1}
              height={7}
              rx={1.4}
              fill={hot ? heat : colors.surfaceRaised}
              stroke={hot ? heat : jackpot ? "rgba(255,212,94,0.45)" : "#33333c"}
              strokeWidth={0.35}
              style={
                hot
                  ? { filter: `drop-shadow(0 0 4px ${heat})` }
                  : jackpot
                    ? { filter: "drop-shadow(0 0 1.5px rgba(255,212,94,0.5))" }
                    : undefined
              }
            />
            <text
              x={x}
              y={BUCKET_TOP + 5.9}
              textAnchor="middle"
              fontSize={m >= 100 ? 2.1 : 2.5}
              fontWeight={700}
              fill={hot ? colors.bg : heat}
            >
              {m >= 10 ? m.toFixed(0) : m}
            </text>
          </g>
        );
      })}

      {/* the ball — squash on contact, stretch in flight, soft shadow */}
      {ball && (
        <>
          <ellipse
            cx={ball.x}
            cy={BUCKET_TOP + 7.6}
            rx={2.1}
            ry={0.55}
            fill="#000"
            opacity={0.5}
          />
          <circle cx={ball.x} cy={ball.y} r={BALL_R * 1.9} fill={colors.accent} opacity={0.12} />
          <ellipse
            cx={ball.x}
            cy={ball.y}
            rx={BALL_R * (2 - ball.squash)}
            ry={BALL_R * ball.squash}
            fill="url(#ballGrad)"
            style={{ filter: `drop-shadow(0 0 3px ${colors.accentGlow})` }}
          />
        </>
      )}
    </svg>
  );
}
